import { articles as rawArticles } from "@/mocks/articles";
import { works as rawWorks } from "@/mocks/works";
import { workGenreLinks } from "@/mocks/workGenres";
import {
  getContentTypeName,
  getContentTypes,
  getGenreById,
  getGenreGroups,
  getGenresForAudience,
  getWorkGenreNames,
  getWorkGenres,
} from "@/lib/taxonomy";
import { isReleased } from "@/lib/releaseDate";
import { DEMO_PROVIDER_KEY, buildDemoProviderId, getWorkRefKey } from "@/lib/workIdentity";
import type {
  Article,
  AudienceType,
  ContentTypeId,
  Work,
} from "@/types/work";
import type { AudienceId, Genre } from "@/types/taxonomy";

type RawWork = Omit<
  Work,
  "genreIds" | "audienceIds" | "attributes" | "providerKey" | "providerId" | "serviceId" | "floorId"
>;

const linkMap = new Map(workGenreLinks.map((link) => [link.workId, link]));

/**
 * 表示用の作品データ。
 * ここで「作品本体」と「ジャンルリンク / 詳細属性」を結合している。
 * 外部APIへ移行する場合は、この結合元をAPIレスポンスに差し替えるだけでよい。
 */
export const works: Work[] = (rawWorks as unknown as RawWork[]).map((raw) => {
  const link = linkMap.get(raw.id);
  const audienceIds: AudienceId[] = link?.audienceIds ?? [raw.audienceType];
  const firstGenre = link?.genreIds?.[0] ? getGenreById(link.genreIds[0]) : undefined;
  return {
    ...raw,
    // 外部API連携後は提供元のIDをそのまま入れる。デモでは内部IDと別の値を保持し、
    // サイト内IDと提供元IDが混ざらないことを構造で示している。
    providerKey: DEMO_PROVIDER_KEY,
    providerId: buildDemoProviderId(raw.id),
    serviceId: raw.contentType,
    floorId: firstGenre?.floorId ?? null,
    genreIds: link?.genreIds ?? [],
    audienceIds,
    attributes: link?.attributes ?? [],
  };
});

export const articles = rawArticles as unknown as Article[];

/** 作品ID（大小文字を無視）の索引。保存IDの解決を O(1) にする */
const workById = new Map<string, Work>(works.map((work) => [work.id.toLowerCase(), work]));

/** 提供元の複合キー（providerKey + serviceId + providerId）の索引 */
const workByRefKey = new Map<string, Work>(works.map((work) => [getWorkRefKey(work), work]));

/** デモデータの基準日（実データ連携時はサーバー時刻に置き換える） */
export const SITE_TODAY = new Date("2026-10-17T12:00:00+09:00");

export const CONTENT_TYPES = getContentTypes();
export const AUDIENCES: AudienceType[] = ["male", "female"];

/** 対象モードで作品を表示してよいか（「共通」は両モードに表示する） */
export function matchesAudience(work: Work, mode: AudienceType): boolean {
  return work.audienceIds.includes(mode) || work.audienceIds.includes("common");
}

/**
 * 保存された作品IDを作品データに解決する。
 * 外部APIに移行して商品が消えた場合でもエラーにせず、missingIds として返す。
 */
export interface ResolvedWorks {
  works: Work[];
  missingIds: string[];
}

export function resolveWorkIds(ids: string[], limit = 0): ResolvedWorks {
  const works: Work[] = [];
  const missingIds: string[] = [];
  ids.forEach((id) => {
    const work = getWorkById(id);
    if (work) works.push(work);
    else missingIds.push(id);
  });
  return {
    works: limit > 0 ? works.slice(0, limit) : works,
    missingIds,
  };
}

export function getWorkById(id: string): Work | undefined {
  if (!id) return undefined;
  return workById.get(id.toLowerCase()) ?? workByRefKey.get(id);
}

export function getWorksByMode(mode: AudienceType): Work[] {
  return works.filter((work) => matchesAudience(work, mode));
}

export function getArticleBySlug(slug: string): Article | undefined {
  return articles.find((article) => article.slug === slug);
}

/** 対象モードで利用できる公式ジャンル（データから動的生成） */
export function getGenreList(mode?: AudienceType): Genre[] {
  return getGenresForAudience(mode);
}

export function getGenreGroupList(mode?: AudienceType) {
  return getGenreGroups(mode);
}

export function getPopularGenres(mode: AudienceType, limit = 12): Genre[] {
  const counts = new Map<string, number>();
  getWorksByMode(mode).forEach((work) => {
    getWorkGenres(work).forEach((genre) => counts.set(genre.id, (counts.get(genre.id) ?? 0) + 1));
  });
  return getGenresForAudience(mode)
    .map((genre) => ({ genre, count: counts.get(genre.id) ?? 0 }))
    .filter((entry) => entry.count > 0)
    .sort((a, b) => b.count - a.count || a.genre.sortOrder - b.genre.sortOrder)
    .slice(0, limit)
    .map((entry) => entry.genre);
}

export function countWorksByGenre(mode: AudienceType, genreId: string): number {
  return getWorksByMode(mode).filter((work) => work.genreIds.includes(genreId)).length;
}

export function countWorksByContentType(mode: AudienceType, contentType: ContentTypeId): number {
  return getWorksByMode(mode).filter((work) => work.contentType === contentType).length;
}

export function getMakers(mode?: AudienceType): string[] {
  const pool = mode ? getWorksByMode(mode) : works;
  return Array.from(new Set(pool.map((work) => work.maker))).sort((a, b) => a.localeCompare(b, "ja"));
}

export interface PersonEntry {
  name: string;
  roles: string[];
}

export function getPeople(mode?: AudienceType): PersonEntry[] {
  const pool = mode ? getWorksByMode(mode) : works;
  const map = new Map<string, Set<string>>();
  pool.forEach((work) => {
    if (!map.has(work.creator)) map.set(work.creator, new Set());
    map.get(work.creator)?.add("作者");
    work.performers.forEach((performer) => {
      if (!map.has(performer)) map.set(performer, new Set());
      map.get(performer)?.add("出演者");
    });
  });
  return Array.from(map.entries())
    .map(([name, roles]) => ({ name, roles: Array.from(roles) }))
    .sort((a, b) => a.name.localeCompare(b.name, "ja"));
}

export function getSeries(mode?: AudienceType): string[] {
  const pool = mode ? getWorksByMode(mode) : works;
  return Array.from(new Set(pool.map((work) => work.series).filter((s): s is string => Boolean(s)))).sort(
    (a, b) => a.localeCompare(b, "ja"),
  );
}

export function getWeeklyAdded(mode: AudienceType): Work[] {
  return getWorksByMode(mode)
    .filter((work) => daysBetween(work.addedAt, SITE_TODAY) <= 7)
    .sort((a, b) => new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime());
}

export function getRecentlyReleased(mode: AudienceType, limit = 8): Work[] {
  return getWorksByMode(mode)
    .filter((work) => isReleased(work.releaseDate, SITE_TODAY))
    .slice()
    .sort((a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime())
    .slice(0, limit);
}

export function getRecentlyUpdated(mode: AudienceType, limit = 8): Work[] {
  return getWorksByMode(mode)
    .slice()
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, limit);
}

function sharesAudience(a: Work, b: Work): boolean {
  if (a.audienceIds.includes("unclassified") || b.audienceIds.includes("unclassified")) return false;
  if (a.audienceIds.includes("common") || b.audienceIds.includes("common")) return true;
  return a.audienceIds.some((id) => b.audienceIds.includes(id));
}

export function getRelatedWorks(work: Work, limit = 4): Work[] {
  return works
    .filter((item) => item.id !== work.id && sharesAudience(item, work))
    .map((item) => {
      const sharedGenres = item.genreIds.filter((id) => work.genreIds.includes(id));
      const sameCreator = item.creator === work.creator;
      const sameSeries = Boolean(work.series) && item.series === work.series;
      const sameMaker = item.maker === work.maker;
      const sameType = item.contentType === work.contentType;
      const score =
        sharedGenres.length * 3 + (sameCreator ? 4 : 0) + (sameSeries ? 6 : 0) + (sameMaker ? 1 : 0) + (sameType ? 1 : 0);
      const reasons: string[] = [];
      if (sameSeries) reasons.push("同じシリーズ");
      if (sameCreator) reasons.push("同じ作者");
      if (sharedGenres.length > 0) reasons.push(`共通ジャンル${sharedGenres.length}件`);
      if (!sameSeries && !sameCreator && sharedGenres.length === 0 && sameMaker) reasons.push("同じメーカー");
      if (reasons.length === 0) reasons.push(`同じ作品形式（${getContentTypeName(item.contentType)}）`);
      return { work: item, score, reasons };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.work);
}

export function getSimilarityReasons(source: Work, target: Work): string[] {
  const reasons: string[] = [];
  if (source.series && source.series === target.series) reasons.push("同じシリーズ");
  if (source.creator === target.creator) reasons.push("同じ作者");
  const shared = target.genreIds.filter((id) => source.genreIds.includes(id));
  if (shared.length > 0) reasons.push(`共通ジャンル${shared.length}件`);
  if (reasons.length === 0 && source.maker === target.maker) reasons.push("同じメーカー");
  if (reasons.length === 0) reasons.push(`同じ作品形式（${getContentTypeName(target.contentType)}）`);
  return reasons;
}

export function getArticlesForWork(work: Work): Article[] {
  return articles.filter((article) => article.relatedWorkIds.includes(work.id));
}

export function getFeatureArticles(limit = 3): Article[] {
  return articles.slice(0, limit);
}

export function daysBetween(from: string, to: Date): number {
  const fromTime = new Date(from).getTime();
  return Math.floor((to.getTime() - fromTime) / (1000 * 60 * 60 * 24));
}

export function isWithinDays(dateString: string, days: number, reference: Date = SITE_TODAY): boolean {
  return daysBetween(dateString, reference) <= days && daysBetween(dateString, reference) >= -1;
}

export { getWorkGenreNames, getWorkGenres };